import styles from './PostBookingResultsWrapper.module.scss';
import {
  CabinCategoryCfmModel,
  OBSObjI,
  PillConfigurations,
  PostBookingResultsProps,
} from '@msc-b2c/interfaces/types';
import ObsResultsContainer from './variations/ObsResultsContainer';
import ExcursionResultsContainer from './variations/ExcursionResultsContainer';
import { PlanMyCruisePagination } from './components/PlanMyCruisePagination';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Text } from '@msc-b2c/shared-components';

import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  breakpoints,
  categoryFiltersSpecificDay,
  defaultPillsVisibility,
  getActiveSession,
  getCardSection,
  managePlurals,
  mscGet,
  mscRemove,
  myAreaAvailableServiceTypeInPage,
  pillsVisibility,
  prepaidCardType,
  RootState,
  scrollToTop,
  setErrorManagerCode,
  setRelatedObsTitlesMap,
  tooltipsVisibility,
  triggerFilterApplication,
  triggerFilterClearing,
  triggerOpenFilterDrawer,
  triggerShowEmptyNote,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  CabinI,
  GetBookingMyMscResponse,
  MyMscExcursion,
  RetrieveVoyagerClubInfoMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import {
  buildDynamicCategoryFilters,
  cabinOfTheLoggedUser,
  getCabinIdOfTheLoggedUser,
  getDepartureDate,
  isXDaysBeforeDeparture,
} from '../utils/myAreaUtils';
import { BookingStatusEnum, ErrorManagerCodeEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { triggerViewItemList } from '../utils/myAreaAnalytics';
import { filterMyAreaExcursionsByDay } from '../utils/excursionFilterUtils';
import { getPriceFromPriceBasis } from './utils/PostBookingResultsUtilities';
import { isHiddenMinorObs } from './utils/allInclusiveCmsHelpers';
import { getBookedExcursionCodesForActivePassenger } from '../utils/utils';
import { filterPills, getPillPriority } from '../utils/pillManagementUtils';
import { AllTypes } from '../productcarousel/ProductCarousel';

type ViewItemListCard = AllTypes & {
  index?: number;
  promoPills?: string;
  itemCategory2?: string;
};

type DemLoginData = {
  type?: string;
};

const viewItemListCardsByInstance = new Map<string, ViewItemListCard[]>();
const viewItemListMountedInstances = new Set<string>();
let viewItemListTimeout: ReturnType<typeof setTimeout> | undefined;
let previousViewItemListKey = '';

const getViewItemListCardsKey = (cards: { code?: string }[]) =>
  cards
    .map((card) => card.code)
    .sort()
    .join(',');

const registerViewItemListTrackingInstance = (instanceKey: string) => {
  viewItemListMountedInstances.add(instanceKey);
};

const scheduleViewItemListTracking = (
  instanceKey: string,
  cards: ViewItemListCard[],
  booking: GetBookingMyMscResponse,
  cabinDetails: CabinCategoryCfmModel | null,
  membershipData?: RetrieveVoyagerClubInfoMyMSCResponse | null
) => {
  viewItemListCardsByInstance.set(instanceKey, cards);

  if (viewItemListTimeout) {
    clearTimeout(viewItemListTimeout);
  }

  viewItemListTimeout = setTimeout(() => {
    if (viewItemListCardsByInstance.size === 0) {
      return;
    }

    const combinedCards = Array.from(viewItemListCardsByInstance.values())
      .flat()
      .map((card, index) => ({ ...card, index: index + 1 }));

    const viewItemListKey = `${getViewItemListCardsKey(combinedCards)}`;

    if (combinedCards.length > 0 && viewItemListKey !== previousViewItemListKey) {
      previousViewItemListKey = viewItemListKey;
      triggerViewItemList(combinedCards, booking, null, cabinDetails, membershipData);
    }
  }, 250);
};

const removeViewItemListTrackingInstance = (instanceKey: string) => {
  viewItemListMountedInstances.delete(instanceKey);
  viewItemListCardsByInstance.delete(instanceKey);
};

export function PostBookingResultsWrapper({
  id = '',
  prepaidType,
  buttonLabel,
  resultsCount,
  blueBannerXF,
  specialNeedsXF,
  obsSecondaryType, // OBS
  noResultXF,
  cardsPerResult,
  goToFirstIcon,
  goToLastIcon,
  goToNextIcon,
  goToPrevIcon,
  filterLabel,
  filterIcon,
  filterModalLeftButton,
  modalTitle = '',
  modalBodyText = '',
  modalButtonLabel = '',
  bookedPillEnabled = true,
  promoPillEnabled = true,
  countdownPillEnabled = true,
  promoTooltipEnabled = true,
  sectionTitle,
  sectionDescription,
  passengerDetailsSeparator,
}: Readonly<PostBookingResultsProps>) {
  const dispatch = useDispatch<AppDispatch>();

  // State Variables
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedCards, setLoadedCards] = useState<MyMscExcursion[]>([]);
  const [obsFilteredByType, setObsFilteredByType] = useState<OBSObjI[]>([]);
  const [noResultsContent, setNoResultsContent] = useState('');
  const [specialNeedsContent, setSpecialNeedsContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(globalThis.innerWidth <= breakpoints.md);
  const [textResultCount, setTextResultCount] = useState(resultsCount?.split('||')[2] ?? '');
  const [loaded, setLoaded] = useState(false);
  const categoryFiltersInitial = useSelector(
    (state: RootState) => state.myAreaFilter.categoryFilters?.initial
  );

  const filtersApplied = useSelector(
    (state: RootState) => state.myAreaFilter.categoryFilters?.applied
  );
  // Constants
  const NUMBER_OF_RESULTS_PLACEHOLDER = '{count}';
  const BLUE_BANNER_QUERY = '.cardWrapper .experience-fragment';

  // Refs
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const blueBanner = useRef<HTMLElement | null>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const previousCardsKeyRef = useRef<string>('');
  const viewItemListInstanceKeyRef = useRef(id);
  const specialNeedsContainerRef = useRef<HTMLDivElement | null>(null);

  // Stable empty arrays to avoid new references on each render
  const EMPTY_EXCURSIONS: MyMscExcursion[] = useMemo(() => [], []);
  const EMPTY_OBS: OBSObjI[] = useMemo(() => [], []);

  // Selectors
  const cabinDetails = useSelector((state: RootState) => state.myCruise.cabinDetails);
  const selectedDay = useSelector((state: RootState) => state.myCruise.selectedDayFilter);
  const myMscExcursionsFromStore = useSelector((state: RootState) => state.myCruise.excursions);
  const myMscObsFromStore = useSelector((state: RootState) => state.myCruise.obs);
  const membershipData = useSelector((state: RootState) => state.myCruise.membershipData);
  const allInclusiveCmsItems = useSelector(
    (state: RootState) => state.myCruise.allInclusiveCmsItems
  );
  const myMscExcursionsData = myMscExcursionsFromStore?.excursions;
  const myMscObsData = myMscObsFromStore?.onboardServices;
  const myMscExcursions = myMscExcursionsData ?? EMPTY_EXCURSIONS;
  const myMscObs = myMscObsData ?? EMPTY_OBS;
  const prepaidApiResponsesCompleted =
    myMscExcursionsFromStore != null && myMscObsFromStore != null;
  const showNoResult = useSelector((state: RootState) => state.myAreaNotifications.showNoResult);
  const {
    prePaidCardProps: propertiesFromPlanMyCruiseContainer,
    pillsVisibility: myAreaPillsVisibilityConfig,
    categoryOrder,
    cardSortingList: cardSortingListRaw,
  } = useSelector((state: RootState) => state.myAreaCommonConfig);
  const cardSortingList = cardSortingListRaw;
  const myAreaPillsVisibility = myAreaPillsVisibilityConfig[id] ?? defaultPillsVisibility;
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);

  const showZeroResults = useSelector(
    (state: RootState) => state.myAreaNotifications.showZeroResultsNote
  );

  const { alreadyPurchasedSectionRouteMapping } = useSelector(
    (state: RootState) => state.navigationMenuData
  );

  const { bookingNumber = '', loginMode } = getActiveSession();
  const isFullLoginSession =
    loginMode === LoginMode.FULL ||
    (typeof loginMode === 'string' && loginMode.toLowerCase() === LoginMode.FULL);
  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const [isFirstRender, setIsFirstRender] = useState(true);

  // AEM props
  const obsResultsAEMProps = {
    buttonLabel,
    blueBannerXF,
    obsSecondaryType,
    cardsPerResult,
  };
  const excursionResultsAEMProps = {
    buttonLabel,
    blueBannerXF,
    noResultXF,
    cardsPerResult,
  };

  const paginationProps = {
    goToFirstIcon,
    goToLastIcon,
    goToNextIcon,
    goToPrevIcon,
  };
  const clearAllFiltersLabel = filterModalLeftButton || 'Clear all';

  let dynamicCategoryFilters;

  const removeSkeleton = () => {
    if (
      showNoResult ||
      (prepaidType === 'Excursions' && myMscExcursions.length > 0) ||
      (prepaidType === 'OBS' && myMscObs.length > 0)
    ) {
      const resultCountSkeletonPlaceholder = document.querySelector(
        '.filterAndResultsSkeletonPlaceholder'
      );
      if (resultCountSkeletonPlaceholder) resultCountSkeletonPlaceholder.remove();
      document
        .querySelectorAll('.cmp-prepaidcardcontainer .postBookingCardSkeleton')
        .forEach((element) => element.remove());
      document
        .querySelectorAll('.cmp-skeleton--prepaidcardcontainer')
        .forEach((element) => element.classList.remove('cmp-skeleton--prepaidcardcontainer'));
    }
    setLoaded(true);
  };

  // Update the result count display
  const updateResultCount = () => {
    if (resultsCount && apiResultsAfterDayFilter) {
      if (apiResultsAfterDayFilter.length > 1) {
        const resultsCountPlural = managePlurals(2, resultsCount).replace(
          NUMBER_OF_RESULTS_PLACEHOLDER,
          apiResultsAfterDayFilter.length.toString()
        );
        setTextResultCount(resultsCountPlural);
      } else if (apiResultsAfterDayFilter.length === 1) {
        const resultsCountSingular = managePlurals(1, resultsCount).replace(
          NUMBER_OF_RESULTS_PLACEHOLDER,
          apiResultsAfterDayFilter.length.toString()
        );
        setTextResultCount(resultsCountSingular);
      } else if (apiResultsAfterDayFilter.length === 0 && (showNoResult || showZeroResults)) {
        setTextResultCount('');
      }
    }
  };

  const buildObsRelatedItemsMap = (obsList: OBSObjI[]): Record<string, string[]> => {
    // Create a map of code -> obs for quick lookup
    const obsByCode = new Map(obsList.map((obs) => [obs.code, obs]));

    const relatedItemsMap: Record<string, string[]> = {};

    obsList.forEach((obs) => {
      const serviceCodes = obs.prepaidItems?.serviceCodes;
      if (!serviceCodes) return;

      // Collect related codes from adults and minors arrays (if exist)
      const relatedCodes = [...(serviceCodes.adults ?? []), ...(serviceCodes.minors ?? [])].filter(
        (code) => code !== obs.code
      ); // exclude self code just in case

      if (relatedCodes.length === 0) return;

      // Keep only the first related package to mirror add-to-cart behavior.
      const firstRelatedDescription = relatedCodes
        .map((code) => obsByCode.get(code)?.description ?? null)
        .find((desc): desc is string => Boolean(desc));

      if (firstRelatedDescription && obs.code) {
        relatedItemsMap[obs.code] = [firstRelatedDescription];
      }
    });

    return relatedItemsMap;
  };

  // Effect for setting the prepaid card type on initial render
  useEffect(() => {
    dispatch(prepaidCardType(prepaidType));
  });

  // Pills and Tooltips Visibility
  useEffect(() => {
    dispatch(
      pillsVisibility({
        [id]: {
          bookedPillEnabled,
          promoPillEnabled,
          countdownPillEnabled,
        },
      })
    );

    dispatch(
      tooltipsVisibility({
        [id]: {
          promoTooltipEnabled,
        },
      })
    );
  }, []);

  useEffect(() => {
    const relatedObs = buildObsRelatedItemsMap(myMscObs);
    dispatch(setRelatedObsTitlesMap(relatedObs));
  }, [myMscObs]);

  useEffect(() => {
    scrollToTop();
  }, [currentPage]);

  // Effect for handling window resize and mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      const nowMobile = globalThis.innerWidth <= breakpoints.md;

      if (nowMobile !== isMobile) {
        setIsMobile(nowMobile);
        setCurrentPage(1);
        setLoadedCards(myMscExcursions.slice(0, cardsPerResult));
      }
    };

    globalThis.addEventListener('resize', handleResize);
    return () => globalThis.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Effect for fetching external noResult content if exists
  useEffect(() => {
    if (noResultXF) {
      const fetchXf = async (): Promise<void> => {
        try {
          const baseURL = globalThis.location.origin;
          const url = `${baseURL}${noResultXF}.content.html`;
          const response = await fetch(url);
          const data = await response.text();
          setNoResultsContent(data);
        } catch (error) {
          console.error('Fetch error:', error);
        }
      };

      fetchXf();
    }
  }, [noResultXF]);

  useEffect(() => {
    if (specialNeedsXF) {
      const fetchXf = async (): Promise<void> => {
        try {
          const baseURL = globalThis.location.origin;
          const url = `${baseURL}${specialNeedsXF}.content.html`;
          const response = await fetch(url);
          const data = await response.text();
          setSpecialNeedsContent(data);
        } catch (error) {
          console.error('Fetch error:', error);
        }
      };

      fetchXf();
    }
  }, [specialNeedsXF]);

  useLayoutEffect(() => {
    if (!specialNeedsContent) return;

    const applyLoginModeClass = () => {
      const root = specialNeedsContainerRef.current;
      if (!root) return;

      const light = loginMode === LoginMode.LIGHT;
      const modeClass = light ? 'isLightLogin' : 'isFullLogin';

      const iconTeasers = root.querySelectorAll('.teaser.cmp-teaser__icon-teaser-cta');
      const simpleCardTeasers = root.querySelectorAll('.teaser.cmp-teaser__simple-card');

      Array.from(iconTeasers)
        .concat(Array.from(simpleCardTeasers))
        .forEach((teaser) => {
          teaser.classList.remove('isLightLogin', 'isFullLogin');
          teaser.classList.add(modeClass);
        });
    };

    applyLoginModeClass();
    /* XF HTML can land a tick late; second pass keeps full-login / light-login hide-* rules working */
    requestAnimationFrame(applyLoginModeClass);
  }, [specialNeedsContent, loginMode]);

  // Effect for filtering excursions after changes in cardsPerResult
  useEffect(() => {
    setLoadedCards(myMscExcursions.slice(0, cardsPerResult));
  }, [cardsPerResult]);

  // Effect for Infinite Scroll (loading more results when near the bottom of the list)
  useEffect(() => {
    if (!isMobile || loadedCards.length >= myMscExcursions.length || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsLoading(true);
          setTimeout(() => {
            setCurrentPage((prevPage) => {
              const nextPage = prevPage + 1;
              const newCards = myMscExcursions.slice(
                prevPage * cardsPerResult,
                nextPage * cardsPerResult
              );
              setLoadedCards((prevCards) => [...prevCards, ...newCards]);
              setIsLoading(false);
              return nextPage;
            });
          }, 1000);
        }
      },
      { threshold: 1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current as Element);
    }

    return () => observer.disconnect();
  }, [loadedCards, isLoading]);

  const isFilterConfigured = (): boolean => {
    return !!filterLabel && !!filterIcon;
  };

  let apiResultsAfterDayFilter: MyMscExcursion[] | OBSObjI[] | undefined = [];

  const filterExcursions = () => {
    const cabinIDOfTheLoggedUser = booking ? getCabinIdOfTheLoggedUser(booking) : '';
    const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
      booking?.cabins,
      cabinIDOfTheLoggedUser
    );

    return filterMyAreaExcursionsByDay(
      myMscExcursions,
      selectedDay,
      cabinLoggedUser,
      propertiesFromPlanMyCruiseContainer.disableEmbarkAndDisembarkExcursions
    );
  };

  apiResultsAfterDayFilter = prepaidType !== 'OBS' ? filterExcursions() : obsFilteredByType ?? [];

  if (prepaidType !== 'OBS') {
    dynamicCategoryFilters = buildDynamicCategoryFilters(
      apiResultsAfterDayFilter,
      categoryFiltersInitial
    );
    if (dynamicCategoryFilters.length > 0)
      dispatch(categoryFiltersSpecificDay(dynamicCategoryFilters));

    // if filter is applied, filter by category filters is present, filter by category filters. The category filter must be checked
    if (filtersApplied && filtersApplied?.length > 0 && isFilterConfigured()) {
      apiResultsAfterDayFilter = apiResultsAfterDayFilter
        ?.filter(
          (exc): exc is MyMscExcursion => (exc as MyMscExcursion).activityGroup !== undefined
        )
        ?.filter((exc: MyMscExcursion) => {
          return filtersApplied?.some((categoryFilter) => {
            return exc.activityGroup?.details === categoryFilter.filterType;
          });
        });
    }
  }

  const totalPages = apiResultsAfterDayFilter
    ? Math.ceil(apiResultsAfterDayFilter.length / cardsPerResult)
    : 0;

  // Effect for updating the result count
  useEffect(() => {
    updateResultCount();
  }, [apiResultsAfterDayFilter, showZeroResults]);

  useEffect(() => {
    //reset the pagination current page to first page after filter is applied
    setCurrentPage(1);
  }, [selectedDay]);

  useEffect(() => {
    if (!prepaidApiResponsesCompleted) {
      return;
    }

    // Check if no results after filtering and the empty notification is missing
    if (booking && !isXDaysBeforeDeparture(getDepartureDate(booking))) {
      if (prepaidType !== 'OBS' && myMscExcursions?.length > 0) {
        if (myMscExcursions?.length > 0 && apiResultsAfterDayFilter?.length === 0) {
          dispatch(triggerShowEmptyNote(true));
        } else {
          dispatch(triggerShowEmptyNote(false));
        }
        removeSkeleton();
      } else if (prepaidType === 'OBS' && myMscObs?.length > 0) {
        const obsFilteredByTypeTemp: OBSObjI[] = [];
        obsSecondaryType?.forEach((obsType) => {
          const filteredObs = myMscObs.filter(
            (obs) =>
              obs.type.code === obsType.type &&
              obs.quantity &&
              obs.quantity > 0 &&
              // Minor ALLINC packages are never shown as their own card; they only appear
              // merged into the linked adult package.
              !isHiddenMinorObs(obs, myMscObs, allInclusiveCmsItems)
          );
          if (filteredObs.length !== 0) {
            obsFilteredByTypeTemp.push(...filteredObs);
            const typeCodes = Array.from(new Set(filteredObs?.map((obs) => obs.type.code ?? '')));
            dispatch(myAreaAvailableServiceTypeInPage(typeCodes));
          }
        });
        if (obsFilteredByTypeTemp.length === 0) {
          dispatch(triggerShowEmptyNote(true));
        } else {
          dispatch(triggerShowEmptyNote(false));
        }
        setObsFilteredByType(obsFilteredByTypeTemp);
        removeSkeleton();
      } else if (showNoResult) {
        removeSkeleton();
        dispatch(triggerShowEmptyNote(true));
      }
    }
  }, [
    selectedDay,
    myMscExcursions,
    myMscObs,
    showNoResult,
    prepaidApiResponsesCompleted,
    allInclusiveCmsItems,
  ]);

  const getCardPrice = (card: AllTypes): number => {
    return Number(getPriceFromPriceBasis(card, '', '').price);
  };

  const filteredPillsMap = useMemo<Record<string, PillConfigurations[]>>(() => {
    const map: Record<string, PillConfigurations[]> = {};
    (apiResultsAfterDayFilter ?? []).forEach((card) => {
      if (card.code) {
        map[card.code] = filterPills(
          card,
          propertiesFromPlanMyCruiseContainer.cardPillConfigurations,
          myAreaPillsVisibility,
          categoryOrder,
          booking,
          cartItems
        );
      }
    });
    return map;
  }, [
    apiResultsAfterDayFilter,
    propertiesFromPlanMyCruiseContainer.cardPillConfigurations,
    myAreaPillsVisibility,
    categoryOrder,
    booking,
    cartItems,
  ]);

  const getCardPillCount = (card: AllTypes): number => {
    if (!booking) return 0;
    return (filteredPillsMap[card.code ?? ''] ?? []).filter(
      (pill) => pill.pillType !== 'itemAlreadyInCart' && pill.pillType !== 'serviceAlreadyInCart'
    ).length;
  };

  // Card Sorting
  const sortedCards = useMemo<AllTypes[]>(() => {
    if (!apiResultsAfterDayFilter || apiResultsAfterDayFilter.length === 0 || !booking) {
      return [];
    }

    const bookedCodes = getBookedExcursionCodesForActivePassenger(booking);

    const groupedByPriority = Array.from(
      { length: cardSortingList.length + 1 },
      () => []
    ) as AllTypes[][];

    apiResultsAfterDayFilter.forEach((card) => {
      const priority = getPillPriority(
        card,
        myAreaPillsVisibility,
        cardSortingList,
        categoryOrder,
        cartItems
      );
      groupedByPriority[priority].push(card);
    });

    const sortedByPriority = groupedByPriority.flatMap((group) => {
      group.sort((a, b) => {
        const pillDiff = getCardPillCount(b) - getCardPillCount(a);
        if (pillDiff !== 0) {
          return pillDiff;
        }

        return getCardPrice(a) - getCardPrice(b);
      });
      return group;
    });
    const isBooked = (card: AllTypes) => {
      const hasBookedPill = (filteredPillsMap[card.code ?? ''] ?? []).some(
        (pill) => pill.pillType?.toUpperCase() === BookingStatusEnum.BOOKED
      );

      return hasBookedPill || (card.code !== undefined && bookedCodes.includes(card.code));
    };

    const nonBooked = sortedByPriority.filter((card) => !isBooked(card));
    const booked = sortedByPriority.filter((card) => isBooked(card));

    return [...nonBooked, ...booked];
  }, [
    apiResultsAfterDayFilter,
    booking,
    cardSortingList,
    myAreaPillsVisibility,
    categoryOrder,
    cartItems,
    filteredPillsMap,
  ]);

  // Pagination of sorted cards
  const paginatedCards = useMemo<AllTypes[]>(() => {
    if (isMobile) {
      return sortedCards;
    } else if (cardsPerResult !== 0) {
      return sortedCards?.slice((currentPage - 1) * cardsPerResult, currentPage * cardsPerResult);
    } else {
      return sortedCards;
    }
  }, [sortedCards, isMobile, cardsPerResult, currentPage]);

  const checkForDEM = () => {
    const dem = mscGet<DemLoginData>('session', 'demLogin');
    if (!dem) return;

    const { type } = dem;
    if (!type) {
      mscRemove('session', 'demLogin');

      return;
    }
    if (type === 'ACT') return;

    const sections: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      'input[name="packages-section"]'
    );
    const section = Array.from(sections).find((s) => new Set(s.value.split(',')).has(type));
    if (!section) {
      mscRemove('session', 'demLogin');

      dispatch(setErrorManagerCode(ErrorManagerCodeEnum.ITEM_NOT_FOUND));
    }
  };

  // localized query for blue banner
  // as we might have many bluebanners in a page
  // use anchorRef as a not dynamically rendered anchor point
  // hide it in case of no results
  useEffect(() => {
    if (!loaded) return; // anchor input isn't mounted yet, don't bother

    const ownContainer = anchorRef.current?.closest('.cmp-prepaidcardcontainer');
    blueBanner.current ??= (ownContainer?.querySelector(BLUE_BANNER_QUERY) as HTMLElement) ?? null;

    if (!blueBanner.current) return;

    const hasNoResults =
      prepaidType === 'OBS'
        ? obsFilteredByType.length === 0
        : apiResultsAfterDayFilter?.length === 0;

    if (hasNoResults || showZeroResults) {
      blueBanner.current.classList.add('hidden');
    } else {
      blueBanner.current.classList.remove('hidden');
    }
  }, [apiResultsAfterDayFilter, obsFilteredByType, showZeroResults, prepaidType, loaded]);

  useEffect(() => {
    if (!paginatedCards || paginatedCards.length < 1 || !isFirstRender) return;
    setIsFirstRender(false);
    checkForDEM();
  }, [paginatedCards, isFirstRender]);

  useEffect(() => {
    const instanceKey = viewItemListInstanceKeyRef.current;
    registerViewItemListTrackingInstance(instanceKey);

    return () => {
      removeViewItemListTrackingInstance(instanceKey);
    };
  }, []);

  useEffect(() => {
    if (!loaded || !booking) return;

    if ((paginatedCards && paginatedCards?.length > 0) || showZeroResults) {
      const indexPaginatedCards = paginatedCards?.map((card, index) => {
        const pills = filteredPillsMap[card.code ?? ''] ?? [];
        const sortedPills = [...pills].sort((a, b) => {
          const aIdx = cardSortingList.indexOf(a.pillType);
          const bIdx = cardSortingList.indexOf(b.pillType);
          if (aIdx === -1 && bIdx === -1) return 0;
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
        return {
          ...card,
          index: (currentPage - 1) * cardsPerResult + index + 1,
          promoPills: sortedPills.map((pill) => pill.pillLabel.toLowerCase().trim()).join(':'),
          itemCategory2:
            'type' in card && card?.type?.code
              ? alreadyPurchasedSectionRouteMapping
                  ?.find((routeItem) => Object.keys(routeItem.routeTypes).includes(card.type.code))
                  ?.routeLabel.toLowerCase()
              : '',
        };
      });

      if (showZeroResults && (!indexPaginatedCards || indexPaginatedCards.length === 0)) {
        scheduleViewItemListTracking(
          viewItemListInstanceKeyRef.current,
          [],
          booking,
          cabinDetails,
          membershipData
        );
      } else if (indexPaginatedCards && indexPaginatedCards.length > 0) {
        const cardsKey = getViewItemListCardsKey(paginatedCards);
        if (cardsKey !== previousCardsKeyRef.current) {
          previousCardsKeyRef.current = cardsKey;
          scheduleViewItemListTracking(
            viewItemListInstanceKeyRef.current,
            indexPaginatedCards,
            booking,
            cabinDetails,
            membershipData
          );
        }
      }
    } else if (!paginatedCards || paginatedCards.length === 0) {
      scheduleViewItemListTracking(
        viewItemListInstanceKeyRef.current,
        [],
        booking,
        cabinDetails,
        membershipData
      );
    }
  }, [
    alreadyPurchasedSectionRouteMapping,
    booking,
    cabinDetails,
    cardSortingList,
    cardsPerResult,
    currentPage,
    filteredPillsMap,
    loaded,
    paginatedCards,
    showZeroResults,
    membershipData,
  ]);

  // TODO: When multiple filters are supported, show noResultXF when no results
  //  are found after filters are applied. Replace condition below.
  // if (/* Put the condition here */) {
  //   return (
  //     <div className={styles.noResultsContainer}>
  //       <div dangerouslySetInnerHTML={{ __html: noResultsContent }} />
  //     </div>
  //   );
  // }

  const finalSortedCards: AllTypes[] = paginatedCards;

  // Clear all is only meaningful on Excursions
  const hasAppliedCategoryFilters =
    prepaidType === 'Excursions' && isFilterConfigured() && (filtersApplied?.length ?? 0) > 0;

  const showResultFilterContainer =
    (Boolean(textResultCount) || (!isMobile && hasAppliedCategoryFilters)) &&
    paginatedCards &&
    paginatedCards.length > 0;

  return (
    loaded && (
      <>
        {paginatedCards && paginatedCards.length > 0 && sectionTitle && (
          <Text
            text={sectionTitle}
            styleOption={'title header-s spacer-mb-016'}
            tagHtml={'h2'}
          />
        )}
        {paginatedCards && paginatedCards.length > 0 && sectionDescription && (
          <Text text={sectionDescription} />
        )}
        {showResultFilterContainer && (
          <div className={[styles['result-filter-container']].join(' ')}>
            <div className={styles['result-container']}>
              {textResultCount && (
                <Text
                  text={textResultCount}
                  styleOption="cmp-text__big bold"
                />
              )}
              {!isMobile && hasAppliedCategoryFilters && (
                <Button
                  text={clearAllFiltersLabel}
                  accessibilityLabel={clearAllFiltersLabel}
                  onClick={() => {
                    dispatch(triggerFilterClearing());
                    dispatch(triggerFilterApplication());
                  }}
                  styleOption={styles.clearAllButton}
                />
              )}
            </div>
            <div className={styles['filter-container']}>
              {isFilterConfigured() && textResultCount && dynamicCategoryFilters?.length > 0 && (
                <Button
                  text={filterLabel}
                  icon={filterIcon}
                  onClick={() => dispatch(triggerOpenFilterDrawer(true))}
                  styleOption={styles.filterButton}
                />
              )}
            </div>
          </div>
        )}

        <input
          type="hidden"
          ref={anchorRef}
          value={getCardSection(prepaidType, obsSecondaryType)}
          name="packages-section"
        />
        {booking &&
          paginatedCards &&
          paginatedCards.length > 0 &&
          (prepaidType === 'OBS' ? (
            <ObsResultsContainer
              postBookingWrapperID={id}
              ObsResultsAEMProps={obsResultsAEMProps}
              PlanMyCruiseData={propertiesFromPlanMyCruiseContainer}
              paginatedCards={finalSortedCards.filter(
                (c): c is OBSObjI => (c as OBSObjI).type !== undefined
              )}
              cardPillConfigurations={propertiesFromPlanMyCruiseContainer.cardPillConfigurations}
              booking={booking}
              modalTitle={modalTitle}
              modalBodyText={modalBodyText}
              modalButtonLabel={modalButtonLabel}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              allCards={sortedCards}
              cardsPerResult={cardsPerResult}
              section={getCardSection(prepaidType, obsSecondaryType)}
              passengerDetailsSeparator={passengerDetailsSeparator}
            />
          ) : (
            <ExcursionResultsContainer
              postBookingWrapperID={id}
              ExcursionResultsAEMProps={excursionResultsAEMProps}
              PlanMyCruiseData={propertiesFromPlanMyCruiseContainer}
              paginatedCards={finalSortedCards.filter(
                (c): c is MyMscExcursion => (c as MyMscExcursion).price !== undefined
              )}
              cardPillConfigurations={propertiesFromPlanMyCruiseContainer.cardPillConfigurations}
              booking={booking}
              modalTitle={modalTitle}
              modalBodyText={modalBodyText}
              modalButtonLabel={modalButtonLabel}
              cardSortingList={cardSortingList}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              allCards={sortedCards}
              cardsPerResult={cardsPerResult}
              passengerDetailsSeparator={passengerDetailsSeparator}
            />
          ))}
        {!isMobile && specialNeedsXF && prepaidType !== 'OBS' && (
          <div
            ref={specialNeedsContainerRef}
            className={`${styles.specialNeedsContainer}${
              isFullLoginSession ? ` ${styles.specialNeedsContainerFullSession}` : ''
            }`}
          >
            <div dangerouslySetInnerHTML={{ __html: specialNeedsContent }} />
          </div>
        )}
        {cardsPerResult !== 0 && !isMobile && (
          <PlanMyCruisePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            {...paginationProps}
          />
        )}
      </>
    )
  );
}

export default PostBookingResultsWrapper;

PostBookingResultsWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/prepaidcardcontainer';
